import { create } from 'zustand'
import { persist } from 'zustand/middleware'

/**
 * Viewer's own privacy/data preference for incoming chat images.
 *
 * When auto-display is on (default), normal images render inline as they arrive. When off,
 * incoming normal images show a tap-to-reveal placeholder — a privacy/data-saver choice about what
 * YOU see; it does not affect what you send.
 *
 * View-limited (ephemeral) images are ALWAYS tap-to-reveal regardless of this setting, because each
 * reveal spends one of the sender's allotted views.
 *
 * A global default with per-conversation overrides. One chat is often the reason somebody wants
 * this off, and turning it off everywhere to deal with one person is a worse answer than turning
 * it off there — so a conversation can disagree with the default, and an override that matches the
 * default is still an override, because the point is that it stops following.
 *
 * Local-only, persisted to localStorage. Same as the global setting has always been: this is a
 * preference about this device's screen, not an account-level fact.
 */
interface ImageDisplayPrefState {
  /** The default for any conversation without an override of its own. */
  autoDisplayChatImages: boolean
  /** Conversation id → its own answer. Absent means "follow the default". */
  conversationOverrides: Record<string, boolean>

  setAutoDisplayChatImages: (value: boolean) => void
  toggleAutoDisplayChatImages: () => void

  /** Give one conversation its own answer, or pass null to make it follow the default again. */
  setConversationOverride: (conversationId: string, value: boolean | null) => void
}

export const useImageDisplayPrefStore = create<ImageDisplayPrefState>()(
  persist(
    (set) => ({
      autoDisplayChatImages: true,
      conversationOverrides: {},

      setAutoDisplayChatImages: (value) => set({ autoDisplayChatImages: value }),
      toggleAutoDisplayChatImages: () =>
        set((state) => ({ autoDisplayChatImages: !state.autoDisplayChatImages })),

      setConversationOverride: (conversationId, value) =>
        set((state) => {
          const next = { ...state.conversationOverrides }
          // Deleted rather than stored as a sentinel, so "follows the default" is the absence of
          // an entry and cannot drift out of step with whatever the default later becomes.
          if (value === null) delete next[conversationId]
          else next[conversationId] = value
          return { conversationOverrides: next }
        }),
    }),
    {
      name: 'adventurecore-image-display-pref',
      // v1 had no overrides. Persisted state from it merges cleanly — the missing key takes the
      // initial {} — so there is nothing to migrate and every existing conversation keeps
      // following the default, which is what it was already doing.
      version: 2,
    }
  )
)

/**
 * Whether images should open by themselves in this conversation.
 *
 * The one place the default and the override are combined, so no caller has to remember which
 * wins. Called without a conversation — a context where the id is not known — it answers with the
 * default, which is the safe reading: the override exists to be stricter than the default in one
 * place, not to relax it everywhere else.
 */
export function useAutoDisplayForConversation(conversationId?: string): boolean {
  return useImageDisplayPrefStore((s) =>
    conversationId !== undefined && conversationId in s.conversationOverrides
      ? s.conversationOverrides[conversationId]
      : s.autoDisplayChatImages
  )
}

/** Whether this conversation has stopped following the default. */
export function useHasConversationOverride(conversationId?: string): boolean {
  return useImageDisplayPrefStore(
    (s) => conversationId !== undefined && conversationId in s.conversationOverrides
  )
}
